import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Password } from 'primereact/password'
import { classNames } from 'primereact/utils'

enum PasswordRequirement {
  LENGTH,
  NUMBER,
  UPPERCASE,
  LOWERCASE,
  SPECIAL,
  WHITESPACE,
}

enum PasswordState {
  VALID,
  INVALID,
}

export const PasswordForm: React.FC<{ loading: boolean; passwordLabel?: string; confirmPasswordLabel?: string }> = ({
  loading,
  passwordLabel = 'Password',
  confirmPasswordLabel = 'Confirm Password',
}) => {
  const { watch, control, getValues } = useFormContext()
  const password = watch('password')

  const [passwordState, setPasswordState] = useState<{ [key in PasswordRequirement]?: PasswordState }>({})

  const validators = useMemo(
    () => ({
      [PasswordRequirement.LENGTH]: (value: string) => value.match(/.{8,}/),
      [PasswordRequirement.NUMBER]: (value: string) => value.match(/\d/),
      [PasswordRequirement.LOWERCASE]: (value: string) => value.match(/[a-z]/),
      [PasswordRequirement.UPPERCASE]: (value: string) => value.match(/[A-Z]/),
      [PasswordRequirement.SPECIAL]: (value: string) => value.match(/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/),
      [PasswordRequirement.WHITESPACE]: (value: string) => value.match(/^\S+.*\S+$/),
    }),
    [],
  )

  useEffect(() => {
    setPasswordState({})
    Object.entries(validators).forEach(([req, validator]) => {
      if (validator(password)) {
        setPasswordState((passwordState) => ({ ...passwordState, [req]: PasswordState.VALID }))
      }
    })
  }, [password, validators])

  const validatePasswordOnSubmit = (password: string) => {
    const errors: string[] = []
    Object.entries(validators).forEach(([req, validator]) => {
      if (!validator(password)) {
        setPasswordState((passwordState) => ({ ...passwordState, [req]: PasswordState.INVALID }))
        errors.push(req)
      }
    })
    return errors.join() || true
  }

  const classNamesForRequirement = (req: PasswordRequirement) => ({
    'email-signup-password-valid': passwordState[req] === PasswordState.VALID,
    'email-signup-password-invalid': passwordState[req] === PasswordState.INVALID,
  })

  return (
    <>
      <div className='auth-input-field'>
        <label htmlFor='password' className='auth-form-label'>
          {passwordLabel}
        </label>
        <Controller
          name='password'
          control={control}
          rules={{
            validate: validatePasswordOnSubmit,
          }}
          render={({ field, fieldState }) => (
            <Password
              disabled={loading}
              placeholder='Enter your password'
              className='auth-password'
              inputClassName={classNames('auth-input', { 'auth-input-error': fieldState.error })}
              id={field.name}
              {...field}
              feedback={false}
              toggleMask={true}
              role='textbox'
              aria-label='Password'
              autoComplete='on'
            />
          )}
        />
      </div>
      <div className='auth-input-field'>
        <label htmlFor='confirmPassword' className='auth-form-label'>
          {confirmPasswordLabel}
        </label>
        <Controller
          name='confirmPassword'
          control={control}
          rules={{
            validate: (e) => {
              return getValues('password') === e
            },
          }}
          render={({ field, fieldState }) => (
            <>
              <Password
                placeholder='Enter your password'
                className='auth-password'
                inputClassName={classNames('auth-input', { 'auth-input-error': fieldState.error })}
                disabled={loading}
                id={field.name}
                {...field}
                feedback={false}
                toggleMask={true}
                role='textbox'
                aria-label='Confirm Password'
                autoComplete='on'
              />
              {fieldState.error ? <div className={`my-1 ${classNames({ 'p-error': fieldState.error })}`}>Passwords must match</div> : ''}
            </>
          )}
        />
      </div>
      <div>
        <span className='email-signup-password-requirements'>Password requirements</span>
        <ul className='email-signup-password-requirements email-signup-password-requirements-list'>
          <li className={classNames(classNamesForRequirement(PasswordRequirement.LENGTH))}>Minimum 8 characters</li>
          <li className={classNames(classNamesForRequirement(PasswordRequirement.NUMBER))}>Contains at least 1 number</li>
          <li className={classNames(classNamesForRequirement(PasswordRequirement.SPECIAL))}>
            Contains at least 1 special character (^ $ * . [ ] {} ( ) ? - " ! @ # % & / \ , {'>'} {'<'} ' : ; | _ ~ ` + =)
          </li>
          <li className={classNames(classNamesForRequirement(PasswordRequirement.UPPERCASE))}>Contains at least 1 uppercase letter</li>
          <li className={classNames(classNamesForRequirement(PasswordRequirement.LOWERCASE))}>Contains at least 1 lowercase letter</li>
          <li className={classNames(classNamesForRequirement(PasswordRequirement.WHITESPACE))}>Must not start or end with whitespace</li>
        </ul>
      </div>
    </>
  )
}
